"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Heart, ShoppingCart, Star, Check, ShoppingCartIcon, ShoppingCartCheck } from "lucide-react"
import Image from "next/image"
import { useCart } from "@/lib/cart-context"
import { useWishlist } from "@/lib/wishlist-context"
import { cn } from "@/lib/utils"
import ProductDetailsDialog from "@/components/productDetails"


interface Product {
  id: number
  name: string
  category: string
  price: number
  originalPrice: number
  discount: number
  rating: number
  image: string
  images?: string[]
  description?: string
  isNew?: boolean
  sizes?: string[]
  colors?: string[]
}

interface ProductCardProps {
  product: Product
  showTimer?: boolean
  timeLeft?: {
    days: number
    hours: number
    minutes: number
    seconds: number
  }
}

export function ProductCard({ product, showTimer, timeLeft }: ProductCardProps) {
  const [isHovered, setIsHovered] = useState(false)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { addToCart, items, removeFromCart } = useCart()
  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist()
  const inWishlist = isInWishlist(product.id)

  const [selectedSize, setSelectedSize] = useState<string>("")
  const [selectedColor, setSelectedColor] = useState<string>("")

  // Set default size and color when modal opens
  useEffect(() => {
    if (isModalOpen) {
      if (product.sizes && product.sizes.length > 0 && !selectedSize) {
        setSelectedSize(product.sizes[0])
      }
      if (product.colors && product.colors.length > 0 && !selectedColor) {
        setSelectedColor(product.colors[0])
      }
    }
  }, [isModalOpen, product.sizes, product.colors, selectedSize, selectedColor])

  const handleModalAddToCart = () => {
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice,
      image: product.image,
      images: productImages,
      category: product.category,
      size: selectedSize || (product.sizes && product.sizes[0]) || undefined,
      color: selectedColor || (product.colors && product.colors[0]) || undefined,
    })
    setIsModalOpen(false) // Close modal after adding to cart
  }

  // Ensure images array exists with fallback
  const productImages = product.images && product.images.length > 0 ? product.images : [product.image]

  // Auto-cycle images on hover
  useEffect(() => {
    let interval: NodeJS.Timeout
    if (isHovered && productImages.length > 1) {
      interval = setInterval(() => {
        setCurrentImageIndex((prev) => (prev + 1) % productImages.length)
      }, 1000)
    } else {
      setCurrentImageIndex(0)
    }

    return () => {
      if (interval) clearInterval(interval)
    }
  }, [isHovered, productImages.length])

  const handleAddToCart = () => {
    const exists = items.find(item => item.id === product.id);

    if (exists) {
      // If already in cart → remove it
      removeFromCart(product.id);
    } else {
      // Otherwise → add it
      addToCart({
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.image,
        images: productImages,
        category: product.category,
        size: (product.sizes && product.sizes[0]) || undefined,
        color: (product.colors && product.colors[0]) || undefined,
      });
    }
  };


  const handleWishlistToggle = () => {
    if (inWishlist) {
      removeFromWishlist(product.id)
    } else {
      addToWishlist({
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.image,
        category: product.category,
      })
      console.log({
        id: product.id,
        name: product.name,
        price: product.price,
        originalPrice: product.originalPrice,
        image: product.image,
        category: product.category,
      }, 'log')
    }
  }

  const currentImage = productImages[currentImageIndex]

  return (
    <Card
      className="group cursor-pointer border-0 shadow-sm hover:shadow-lg transition-all duration-300 bg-white dark:bg-gray-800 w-full max-w-sm mx-auto sm:max-w-none"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardContent className="p-0">
        <div className="relative w-full overflow-hidden rounded-t-lg aspect-[3/4] sm:aspect-[4/5]">
          <Badge className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-green-500 hover:bg-green-500 text-white z-10 text-xs sm:text-sm">
            {product.discount}% off
          </Badge>

          <div
            className={cn(
              "absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-col gap-1 sm:gap-2 transition-opacity duration-300 z-10",
            )}
          >
            <Button
              size="sm"
              variant="secondary"
              className="w-7 h-7 sm:w-8 sm:h-8 p-0 bg-white/80 hover:bg-white dark:bg-gray-700/80 dark:hover:bg-gray-700"
              onClick={handleWishlistToggle}
            >
              <Heart className={cn("h-3 w-3 sm:h-4 sm:w-4", inWishlist && "fill-red-500 text-red-500")} />
            </Button>

            <ProductDetailsDialog
              product={product}
              open={isModalOpen}
              onOpenChange={setIsModalOpen}
              onAddToCart={handleModalAddToCart}
            />

            {
              items.some(item => item.id === product.id) ? (
                <Button
                  size="sm"
                  variant="secondary"
                  className="relative w-7 h-7 sm:w-8 sm:h-8 p-0 bg-white/80 hover:bg-white dark:bg-gray-700/80 dark:hover:bg-gray-700"
                  onClick={handleAddToCart}
                >
                  {/* Shopping Cart */}
                  <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />

                  {/* Check overlay */}
                  <Check className="absolute top-0 right-0 h-2.5 w-2.5 sm:h-3 sm:w-3 text-green-600 bg-white rounded-full" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="secondary"
                  className="w-7 h-7 sm:w-8 sm:h-8 p-0 bg-white/80 hover:bg-white dark:bg-gray-700/80 dark:hover:bg-gray-700"
                  onClick={handleAddToCart}
                >
                  {/* Default cart icon */}
                  <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              )
            }



          </div>

          {showTimer && timeLeft && (
            <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3 bg-orange-200 dark:bg-orange-300 rounded p-1.5 sm:p-2 text-xs z-10 max-w-[calc(100%-4rem)]">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 text-center text-orange-900">
                <div className="min-w-0">
                  <div className="font-bold text-xs sm:text-sm">{String(timeLeft.days).padStart(2, "0")}</div>
                  <div className="text-[10px] sm:text-xs truncate">Days</div>
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-xs sm:text-sm">{String(timeLeft.hours).padStart(2, "0")}</div>
                  <div className="text-[10px] sm:text-xs truncate">Hours</div>
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-xs sm:text-sm">{String(timeLeft.minutes).padStart(2, "0")}</div>
                  <div className="text-[10px] sm:text-xs truncate">Mins</div>
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-xs sm:text-sm">{String(timeLeft.seconds).padStart(2, "0")}</div>
                  <div className="text-[10px] sm:text-xs truncate">Sec</div>
                </div>
              </div>
            </div>
          )}

          <Image
            src={currentImage || "/placeholder.svg"}
            alt={product.name}
            fill
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            sizes="(max-width: 640px) 100vw, (max-width: 768px) 50vw, 33vw"
          />
        </div>

        <div className="p-3 sm:p-4 space-y-1.5 sm:space-y-2">
          <Badge
            variant="secondary"
            className="w-fit text-xs bg-gray-100 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-300 uppercase"
          >
            {product?.category}
          </Badge>
          <h3
            className="font-semibold text-sm sm:text-base text-gray-900 dark:text-white truncate"
            title={product.name}
          >
            {product?.name}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">
              ₦{product.price.toLocaleString()}
            </span>
            <span className="text-xs sm:text-sm text-gray-500 line-through">
              ₦{product?.originalPrice?.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Star className="h-3 w-3 sm:h-4 sm:w-4 fill-amber-400 text-amber-400" />
            <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">{product.rating || 0}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
